import {
  ArrowUpDown,
  ChevronDown,
  CircleAlert,
  TriangleAlert,
  Wand2,
  Wrench,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/common/pagination';
import { SearchInput } from '~/components/common/search-input';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { ProductThumbnail } from '~/features/content/components/quality/product-identity-cell';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { cn } from '~/utils/cn';
import { formatRelative } from '~/utils/date';

import {
  ENRICHMENT_VALIDATION_PAGE_SIZE,
  useEnrichmentValidationFacets,
  useEnrichmentValidationProducts,
  useEnrichmentValidationSummary,
  useRepairEnrichmentRun,
  useScopedValidatedProducts,
  useScopedValidationFacets,
} from '../../hooks/use-enrichment-validation';
import {
  LIVE_VALIDATION_RULES,
  NORMALISED_TO_EMPTY_RULE,
  STRUCTURE_INVALID_RULE,
  humanizeRuleId,
  type ValidationConnectorCode,
} from '../../services/enrichment-validation.service';
import type {
  EnrichmentValidatedProduct,
  EnrichmentValidationIssue,
  EnrichmentValidationProductFilter,
  EnrichmentValidationProductSort,
  EnrichmentValidationSummary,
  ValidationScope,
} from '../../types/enrichment-validation.api.types';

import { ValidationExportButton } from './validation-export-button';
import { FieldChangeList } from './validation-field-changes';

/** Feature flag for one product's "Fix": on once workflow-service's product-scoped repair
 *  (agentic-workflow-service#1010) is live — an older service would repair the whole run. */
const REPAIR_PRODUCT_SCOPE_FLAG = 'catalog_repair_single_product';

/** Radio sentinel for "no filter": rule ids, fields (`*` included) and fates never equal it. */
const ALL = '__all__';

const FILTERS: readonly EnrichmentValidationProductFilter[] = [
  'invalid',
  'normalised',
  'warning',
  'all',
];

const SORTS: readonly EnrichmentValidationProductSort[] = [
  'external_id',
  'title',
  'most_issues',
  'recent',
];

/** Enriched columns offered by the Field menu when the facets can't be read ('*' = whole
 *  product), per connector; each has a label under `enrichmentValidation.drawer.fields`. */
const GMC_FALLBACK_FIELDS: readonly string[] = [
  '*',
  'title',
  'description',
  'structured_title',
  'structured_description',
  'product_highlight',
  'product_detail',
  'product_type',
  'google_product_category',
  'color',
  'material',
  'pattern',
  'size',
  'gender',
  'age_group',
  'brand',
];

/** Meta's: integration-service's Meta repairable columns (`META_REPAIRABLE_COLUMNS`). */
const META_FALLBACK_FIELDS: readonly string[] = [
  '*',
  'title',
  'description',
  'brand',
  'color',
  'material',
  'pattern',
  'size',
  'gender',
  'age_group',
  'google_product_category',
  'product_type',
  'size_type',
  'size_system',
  'style',
  'standard_features',
  'additional_features',
  'custom_label_0',
  'custom_label_1',
  'custom_label_2',
  'custom_label_3',
  'custom_label_4',
  'link',
  'image_link',
  'additional_image_link',
  'category_attributes',
];

const FALLBACK_FIELDS: Record<ValidationConnectorCode, readonly string[]> = {
  GOOGLE_MERCHANT: GMC_FALLBACK_FIELDS,
  META_COMMERCE: META_FALLBACK_FIELDS,
};

/** Keystroke debounce before a search hits the server (Enter commits at once). */
const SEARCH_DEBOUNCE_MS = 300;

/** The server's `q` limit; longer input is cut rather than answered with a 422. */
const SEARCH_MAX_LENGTH = 200;

/** The facets' list cap — the menus say "top 50" when the server truncated past it. */
const FACET_LIMIT = 50;

/** Severity pill tones (outline Badge on design tokens): needs normalising, a warning, or
 *  normalised. */
const PILL_CLASS = {
  critical: 'bg-error-50 text-error border-error/30',
  warning: 'bg-warning-50 text-warning border-warning/30',
  fixable: 'bg-primary/10 text-primary border-primary/30',
} as const;

/**
 * What a products list reads. `run`: one run's products (the batch page's View products), with
 * its page-owned summary. `scopes`: products of several runs at once — each run with the product
 * ids to include (a catalog export's rows) — read in one cross-run query per page.
 */
export type ValidatedProductsSource =
  | {
      kind: 'run';
      /** The run's root id (`pipelineWorkflowId ?? workflowId`) — the summary's key. */
      workflowId: string;
      /** Page-owned summary: the tab counts and the run state the pages are keyed on. */
      summary: EnrichmentValidationSummary | null | undefined;
    }
  | {
      kind: 'scopes';
      scopes: readonly ValidationScope[];
      /** Identifies the scopes (a hash of them): the cache key, however many ids they hold. */
      digest: string;
      /** The batch a product came from, by its scope's `workflowId` — shown on each row. */
      batchName: (workflowId: string) => string;
    };

/** No scopes: the cross-run hooks' input while the source is one run (they stay disabled). */
const NO_SCOPES: readonly ValidationScope[] = [];

interface InvalidProductsViewProps {
  source: ValidatedProductsSource;
  /** The connector of the run(s) listed — every read is scoped to its rule set. */
  connectorCode: ValidationConnectorCode;
  /** Fetch only while shown (a closed drawer, a view not on screen). */
  active: boolean;
  /** `sheet`: inside `InvalidProductsDrawer` (Radix title / description, room for the close
   *  button). `panel`: inside a host's own surface, which owns the dialog semantics. */
  variant?: 'sheet' | 'panel';
}

interface InvalidProductsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The run's root id (`pipelineWorkflowId ?? workflowId`). */
  workflowId: string;
  /** The run's connector (see `InvalidProductsViewProps`). */
  connectorCode: ValidationConnectorCode;
  /** Page-owned summary (see `ValidatedProductsSource`). */
  summary: EnrichmentValidationSummary | null | undefined;
}

/** The tab a list opens on: the first of Needs normalising, Normalised, Warnings only that has
 *  rows, else All products. */
function defaultProductFilter(
  counts: Record<EnrichmentValidationProductFilter, number>,
): EnrichmentValidationProductFilter {
  if (counts.invalid > 0) return 'invalid';
  if (counts.normalised > 0) return 'normalised';
  if (counts.warning > 0) return 'warning';
  return 'all';
}

/** A run summary's per-tab counts. */
function countsOf(
  summary: EnrichmentValidationSummary,
): Record<EnrichmentValidationProductFilter, number> {
  return {
    invalid: summary.invalidProducts,
    normalised: summary.normalisedProducts,
    warning: summary.warningProducts,
    repairable: summary.repairableProducts,
    all: summary.totalProducts,
  };
}

/** The search the server receives: control characters dropped (a 422), trimmed, capped at
 *  200 chars; blank = no search (`null`). */
function normalizeSearch(raw: string): string | null {
  const cleaned = Array.from(raw)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 0x20 && code !== 0x7f;
    })
    .join('')
    .trim()
    .slice(0, SEARCH_MAX_LENGTH)
    .trim();
  return cleaned === '' ? null : cleaned;
}

/** Human label for a rule id: the live rules have copy, a retired id an older verdict still
 *  carries falls back to the humanised id. `nsSeparator: false` keeps a `:` part of the key. */
function useRuleLabel() {
  const { t } = useTranslation('catalog-operations');
  return (rule: string) =>
    t(`enrichmentValidation.rules.${rule}`, {
      defaultValue: humanizeRuleId(rule),
      nsSeparator: false,
    });
}

/** Human label for an issue column: "Whole product" for `*`, a known column's name, else the
 *  raw column with underscores spaced and the first letter capitalised. */
function useFieldLabel() {
  const { t } = useTranslation('catalog-operations');
  return (field: string) => {
    if (field === '*') return t('enrichmentValidation.drawer.rowLevelField');
    const spaced = field.replace(/_/g, ' ');
    const fallback = spaced.charAt(0).toUpperCase() + spaced.slice(1);
    return t(`enrichmentValidation.drawer.fields.${field}`, { defaultValue: fallback });
  };
}

/** Issues grouped by field, in first-seen order (the server sends CRITICAL first). */
function groupByField(issues: EnrichmentValidationIssue[]) {
  const groups = new Map<string, EnrichmentValidationIssue[]>();
  for (const issue of issues) {
    const group = groups.get(issue.field);
    if (group) group.push(issue);
    else groups.set(issue.field, [issue]);
  }
  return [...groups.entries()];
}

/** Matches a quoted value in a message — retired rules quoted values typographically (“…”), a
 *  few older messages use ASCII quotes. The casebook rules never quote the stored value. */
const QUOTED_VALUE = /“([^”]*)”|"([^"]*)"/;

/** Escapes a column name for use inside a RegExp. */
function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A server message split for display: the backend's leading raw column (plus any path such as
 *  `[2]` / `.content`) is dropped, and — only with `extractValue` (a retired rule's message) — the
 *  first quoted value pulled out; the rest is kept as prose. */
function parseIssueMessage(message: string, field: string, extractValue: boolean) {
  let rest = message.trim();
  let path: string | null = null;
  if (field !== '*') {
    const prefix = new RegExp(
      `^${escapeRegExp(field)}((?:\\[[^\\]]*\\]|\\.[A-Za-z_]\\w*)*)(?::\\s*|\\s+)`,
    );
    const match = prefix.exec(rest);
    if (match) {
      const captured = match[1] ?? '';
      path = captured === '' ? null : captured;
      rest = rest.slice(match[0].length);
    }
  }
  let value: string | null = null;
  const quoted = extractValue ? QUOTED_VALUE.exec(rest) : null;
  if (quoted) {
    value = quoted[1] ?? quoted[2] ?? null;
    rest = rest.slice(0, quoted.index) + rest.slice(quoted.index + quoted[0].length);
  }
  const prose = rest
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\s:]+$/, '');
  const explanation = prose ? prose.charAt(0).toUpperCase() + prose.slice(1) : null;
  return { path, value, explanation };
}

/** Per-issue panel tone (tinted background + border + icon colour), on design tokens. */
const ISSUE_TONE = {
  CRITICAL: { panel: 'bg-error-50 border-error/30', icon: 'text-error', pill: PILL_CLASS.critical },
  WARNING: {
    panel: 'bg-warning-50 border-warning/30',
    icon: 'text-warning',
    pill: PILL_CLASS.warning,
  },
  FIX: { panel: 'bg-primary/5 border-primary/30', icon: 'text-primary', pill: PILL_CLASS.fixable },
} as const;

const ISSUE_ICON = { CRITICAL: CircleAlert, WARNING: TriangleAlert, FIX: Wrench } as const;

/** One issue as its own tinted panel: icon + rule label + severity pill, where in the field,
 *  the explanation, a retired rule's quoted current value, and what to do (plus the banner action). */
function IssuePanel({
  issue,
  isFix,
  resolvedBy,
}: {
  issue: EnrichmentValidationIssue;
  isFix: boolean;
  /** The banner action that resolves this issue for this product, if any. */
  resolvedBy: 'fix' | null;
}) {
  const { t } = useTranslation('catalog-operations');
  const ruleLabel = useRuleLabel();
  const tone = isFix ? 'FIX' : issue.severity;
  const Icon = ISSUE_ICON[tone];
  const title = ruleLabel(issue.rule);
  // The live rules never quote the stored value; only a retired rule's message may.
  const { path, value, explanation } = parseIssueMessage(
    issue.message,
    issue.field,
    !LIVE_VALIDATION_RULES.has(issue.rule),
  );
  // The explanation once — skipped when it only repeats the rule label.
  const showExplanation =
    explanation !== null && explanation.toLowerCase() !== title.trim().toLowerCase();
  const indexMatch = path ? /^\[(\d+)\]$/.exec(path) : null;
  const location = indexMatch
    ? t('enrichmentValidation.drawer.issue.item', { position: Number(indexMatch[1]) + 1 })
    : path?.replace(/^\./, '');
  // The rule's help, live rules only (a retired id gets none).
  const help = t(`enrichmentValidation.ruleHelp.${issue.rule}`, {
    defaultValue: '',
    nsSeparator: false,
  });
  return (
    <li
      data-testid={`batch-detail-validation-drawer-issue-${issue.rule}`}
      className={cn('space-y-1.5 rounded-md border px-3 py-2', ISSUE_TONE[tone].panel)}
    >
      <div className="flex items-start gap-2">
        <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', ISSUE_TONE[tone].icon)} />
        <p className="text-foreground min-w-0 flex-1 text-sm leading-5 font-medium break-words">
          {title}
        </p>
        <Badge
          data-testid={`batch-detail-validation-drawer-issue-severity-badge-${issue.rule}`}
          variant="outline"
          className={cn('shrink-0 px-2 py-0 text-[11px]', ISSUE_TONE[tone].pill)}
        >
          {t(`enrichmentValidation.drawer.severity.${issue.severity}`)}
        </Badge>
      </div>
      <div className="space-y-1.5 pl-6">
        {location && (
          <p className="text-muted-foreground text-xs">
            {t('enrichmentValidation.drawer.issue.location', { location })}
          </p>
        )}
        {showExplanation && <p className="text-foreground text-sm break-words">{explanation}</p>}
        {value !== null && (
          <div className="space-y-0.5">
            <p className="text-muted-foreground text-xs">
              {t('enrichmentValidation.drawer.issue.currentValue')}
            </p>
            <p className="bg-background/70 text-foreground rounded border px-2 py-1 font-mono text-xs break-all whitespace-pre-wrap">
              {value}
            </p>
          </div>
        )}
        {(help || resolvedBy) && (
          <p className="text-muted-foreground text-xs">
            <span className="text-foreground font-medium">
              {t('enrichmentValidation.drawer.issue.whatToDo')}
            </span>{' '}
            {help}
            {help && resolvedBy ? ' ' : null}
            {resolvedBy && t(`enrichmentValidation.drawer.issue.resolvedBy.${resolvedBy}`)}
          </p>
        )}
      </div>
    </li>
  );
}

/** One field's issues: the field's human label once as the group header, then one panel per
 *  issue. Messages render only as text nodes. */
function FieldIssueGroup({
  field,
  issues,
  isFix,
  product,
}: {
  field: string;
  issues: EnrichmentValidationIssue[];
  isFix: boolean;
  product: EnrichmentValidatedProduct;
}) {
  const fieldLabel = useFieldLabel();
  return (
    <li data-testid={`batch-detail-validation-drawer-issue-group-${field}`} className="space-y-1.5">
      <p className="text-foreground text-xs font-semibold">{fieldLabel(field)}</p>
      <ul className="space-y-1.5">
        {issues.map((issue, index) => (
          // Issues are a static per-product list (never reordered) and a
          // rule can repeat on a field, so the index completes the key.
          <IssuePanel
            key={`${issue.rule}:${String(index)}`}
            issue={issue}
            isFix={isFix}
            // Fix automatically resolves a normalisation the product's repair writes.
            resolvedBy={
              (isFix ? product.repairable : issue.severity === 'CRITICAL' && product.fixResolves)
                ? 'fix'
                : null
            }
          />
        ))}
      </ul>
    </li>
  );
}

/** One product's single status pill: needs normalising, else warnings, else normalised (valid
 *  with a before / after on record, or repairable under an older verdict). */
function SeverityPill({ product }: { product: EnrichmentValidatedProduct }) {
  const { t } = useTranslation('catalog-operations');
  if (product.criticalCount > 0) {
    return (
      <Badge
        data-testid="batch-detail-validation-drawer-status-badge-critical"
        variant="outline"
        className={PILL_CLASS.critical}
      >
        {t('enrichmentValidation.drawer.needsNormalisingBadge')}
      </Badge>
    );
  }
  if (product.warningCount > 0) {
    return (
      <Badge
        data-testid="batch-detail-validation-drawer-status-badge-warning"
        variant="outline"
        className={PILL_CLASS.warning}
      >
        {t('enrichmentValidation.drawer.warningCount', { count: product.warningCount })}
      </Badge>
    );
  }
  if (product.changes.length > 0 || product.repairable) {
    return (
      <Badge
        data-testid="batch-detail-validation-drawer-status-badge-normalised"
        variant="outline"
        className={PILL_CLASS.fixable}
      >
        {t('enrichmentValidation.drawer.normalisedBadge')}
      </Badge>
    );
  }
  return null;
}

/** The count the pill doesn't carry: warnings beside a "needs normalising" pill. */
function SecondaryCounts({ product }: { product: EnrichmentValidatedProduct }) {
  const { t } = useTranslation('catalog-operations');
  if (product.criticalCount === 0 || product.warningCount === 0) return null;
  return (
    <span className="text-muted-foreground text-xs whitespace-nowrap tabular-nums">
      {t('enrichmentValidation.drawer.warningCount', { count: product.warningCount })}
    </span>
  );
}

/** One product's latest verdict as a collapsible list row: thumbnail, title, external id, one
 *  status pill; expanded, each normalised field before / after, then any other issues. */
function ValidatedProductRow({
  product,
  defaultExpanded,
  batchName = null,
  onFix,
  fixPending = false,
}: {
  product: EnrichmentValidatedProduct;
  defaultExpanded: boolean;
  /** The batch it came from, on a cross-run list; `null` on one run's list. */
  batchName?: string | null;
  /** Fix just this product automatically; absent = no per-product action (the run-level
   *  "Fix automatically" is the only one). */
  onFix?: () => void;
  fixPending?: boolean;
}) {
  const { t } = useTranslation('catalog-operations');
  const fieldLabel = useFieldLabel();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const detailsId = useId();
  const hasChanges = product.changes.length > 0;
  // With a before / after on record, the normalisations (and the "left as stored" warnings) are
  // shown by it; an older verdict without one lists its structure issues as before.
  const shownByChanges = (issue: EnrichmentValidationIssue) =>
    hasChanges &&
    (issue.rule === STRUCTURE_INVALID_RULE || issue.rule === NORMALISED_TO_EMPTY_RULE);
  const problems = groupByField(
    product.issues.filter(
      (issue) => issue.rule !== STRUCTURE_INVALID_RULE && !shownByChanges(issue),
    ),
  );
  const fixes = groupByField(
    product.issues.filter(
      (issue) => issue.rule === STRUCTURE_INVALID_RULE && !shownByChanges(issue),
    ),
  );
  const hasDetails = hasChanges || problems.length > 0 || fixes.length > 0;
  const title = product.title || t('enrichmentValidation.drawer.untitled');

  return (
    <li data-testid={`batch-detail-validation-drawer-row-${product.externalId}`}>
      {/* The title button's `after:` overlay stretches its click target over the whole header;
          the external id sits above it (`z-10`) so its full-value tooltip and selection work. */}
      <div
        className={cn(
          'relative -mx-2 flex items-start gap-3 rounded-md px-2 py-3',
          hasDetails && 'hover:bg-muted/40',
        )}
      >
        <ProductThumbnail
          src={product.imageUrl}
          referrerPolicy="no-referrer"
          // Same 48px box with or without an image: a product icon on a muted square until a
          // verdict carries `imageUrl` (or the image fails), so rows never shift.
          className="size-12 rounded-md [&_svg]:size-5"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-foreground text-sm leading-5 font-medium">
            {hasDetails ? (
              <Button
                type="button"
                data-testid={`batch-detail-validation-drawer-row-toggle-button-${product.externalId}`}
                variant="ghost"
                aria-expanded={expanded}
                aria-controls={detailsId}
                onClick={() => {
                  setExpanded((value) => !value);
                }}
                className="text-foreground h-auto w-full cursor-pointer justify-start rounded-sm p-0 text-left leading-5 font-medium whitespace-normal after:absolute after:inset-0 after:content-[''] hover:bg-transparent focus-visible:ring-offset-0"
              >
                <span className="line-clamp-2 break-words">{title}</span>
              </Button>
            ) : (
              <span className="line-clamp-2 break-words">{title}</span>
            )}
          </h3>
          <p className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            <span
              className="relative z-10 max-w-full truncate font-mono"
              title={product.externalId}
            >
              {product.externalId}
            </span>
            {batchName && (
              <Badge
                variant="outline"
                className="relative z-10 max-w-56 truncate text-[11px] font-normal"
                title={batchName}
              >
                {batchName}
              </Badge>
            )}
            <span>
              {t('enrichmentValidation.drawer.checked', {
                relative: formatRelative(product.validatedAt),
              })}
            </span>
            {product.attempt > 0 && (
              <span>
                {t('enrichmentValidation.drawer.reenrichAttempts', { count: product.attempt })}
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <SeverityPill product={product} />
          <SecondaryCounts product={product} />
        </div>
        {hasDetails && (
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform',
              expanded && 'rotate-180',
            )}
          />
        )}
      </div>
      {hasDetails && (
        // Indented to the text column (48px thumbnail + 12px gap) from `sm`.
        <div
          id={detailsId}
          hidden={!expanded}
          data-testid={`batch-detail-validation-drawer-row-details-${product.externalId}`}
          className="space-y-3 pb-4 sm:pl-15"
        >
          {hasChanges && (
            <FieldChangeList
              changes={product.changes}
              // A valid product's before / after is what code already wrote.
              isApplied={product.isValid}
              fieldLabel={fieldLabel}
            />
          )}
          {problems.length > 0 && (
            <ul aria-label={t('enrichmentValidation.drawer.issuesLabel')} className="space-y-2">
              {problems.map(([field, issues]) => (
                <FieldIssueGroup
                  key={field}
                  field={field}
                  issues={issues}
                  isFix={false}
                  product={product}
                />
              ))}
            </ul>
          )}
          {fixes.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-primary text-xs font-medium">
                  {t('enrichmentValidation.drawer.changes.pending')}
                </p>
                {onFix && !product.isValid && (
                  <Button
                    data-testid={`batch-detail-validation-drawer-row-fix-button-${product.externalId}`}
                    type="button"
                    size="sm"
                    className="h-7 cursor-pointer gap-1.5 px-2.5 text-xs"
                    disabled={fixPending}
                    isLoading={fixPending}
                    onClick={onFix}
                  >
                    {!fixPending && <Wand2 aria-hidden="true" className="size-3.5" />}
                    {t('enrichmentValidation.drawer.fixThisProduct')}
                  </Button>
                )}
              </div>
              <ul
                aria-label={t('enrichmentValidation.drawer.changes.pending')}
                className="space-y-2"
              >
                {fixes.map(([field, issues]) => (
                  <FieldIssueGroup
                    key={field}
                    field={field}
                    issues={issues}
                    isFix
                    product={product}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

interface FacetOption {
  value: string;
  label: string;
  /** Facet count; omitted when the counts aren't loaded (or the value isn't in the top 50). */
  count?: number | undefined;
}

/** Facet options plus the chosen value when the facets don't list it (beyond the top 50, or
 *  zero under the current search), so the menu always shows what is applied. */
function withSelected(
  options: FacetOption[],
  selected: string | null,
  labelFor: (value: string) => string,
): FacetOption[] {
  if (selected === null || options.some((option) => option.value === selected)) return options;
  return [{ value: selected, label: labelFor(selected) }, ...options];
}

/** One compact filter menu (Problem / Field): "All" then each value with its facet count; "Top 50
 *  shown" when the server truncated the list. */
function FacetMenu({
  label,
  allLabel,
  value,
  selectedLabel,
  options,
  truncated,
  state,
  onChange,
  testId,
}: {
  label: string;
  allLabel: string;
  value: string | null;
  selectedLabel: string | null;
  options: FacetOption[];
  truncated: boolean;
  /** Facet read state: with no options, `loading` / `error` show a note instead. */
  state: 'loading' | 'error' | 'ready';
  onChange: (value: string | null) => void;
  /** Automation hook: the trigger gets `${testId}-select`, menu `${testId}-menu`, items `${testId}-item-<value>`. */
  testId: string;
}) {
  const { t } = useTranslation('catalog-operations');
  const note =
    options.length > 0
      ? state === 'error'
        ? t('enrichmentValidation.drawer.filters.countsUnavailable')
        : null
      : state === 'loading'
        ? t('enrichmentValidation.drawer.filters.loading')
        : state === 'error'
          ? t('enrichmentValidation.drawer.filters.loadFailed')
          : t('enrichmentValidation.drawer.filters.noOptions');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* The trigger keeps its label width when a value is chosen (the chip row shows the
            value), so the filter row never reflows; the value stays in the accessible name. */}
        <Button
          data-testid={`${testId}-select`}
          variant="outline"
          size="sm"
          title={selectedLabel ?? undefined}
          aria-label={selectedLabel !== null ? `${label}: ${selectedLabel}` : label}
          className={cn(
            'h-8 shrink-0 cursor-pointer gap-1.5 px-2.5 text-xs whitespace-nowrap',
            value !== null && 'border-primary/40 bg-primary/5 text-primary',
          )}
        >
          <span>{label}</span>
          {value !== null && (
            <span aria-hidden="true" className="bg-primary size-1.5 shrink-0 rounded-full" />
          )}
          <ChevronDown aria-hidden="true" className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid={`${testId}-menu`} align="start" className="max-h-80 w-72">
        <DropdownMenuLabel className="text-muted-foreground flex items-center justify-between gap-2 text-xs font-medium">
          <span>{label}</span>
          {truncated && (
            <span className="font-normal">
              {t('enrichmentValidation.drawer.filters.topN', { count: FACET_LIMIT })}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          data-testid={`${testId}-radio-group`}
          value={value ?? ALL}
          onValueChange={(next) => {
            onChange(next === ALL ? null : next);
          }}
        >
          <DropdownMenuRadioItem
            data-testid={`${testId}-item-all`}
            value={ALL}
            className="cursor-pointer"
          >
            {allLabel}
          </DropdownMenuRadioItem>
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              data-testid={`${testId}-item-${option.value}`}
              value={option.value}
              className="cursor-pointer gap-3"
            >
              <span className="min-w-0 flex-1 truncate" title={option.label}>
                {option.label}
              </span>
              {option.count !== undefined && (
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                  {option.count.toLocaleString()}
                </span>
              )}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {note && (
          <DropdownMenuItem
            data-testid={`${testId}-note`}
            disabled
            className="text-muted-foreground text-xs"
          >
            {note}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A run's validated products for runs of ~300k products: search, result tabs, facet-counted
 *  filter menus, sort, collapsible rows with thumbnails, 50 a page. The content only — shown in
 *  its own drawer (`InvalidProductsDrawer`) or inside a host's panel. Fetches only while active. */
export function InvalidProductsView({
  source,
  connectorCode,
  active,
  variant = 'sheet',
}: InvalidProductsViewProps) {
  const { t } = useTranslation('catalog-operations');
  const ruleLabel = useRuleLabel();
  const fieldLabel = useFieldLabel();
  // `null` = not chosen yet: opens on Needs normalising; when nothing needs it, on Normalised, then
  // Warnings only, then All products — the first tab with rows, so a clean run's View products
  // lists its products instead of an empty tab. Derived, so no reset effect.
  const [chosenStatus, setStatus] = useState<EnrichmentValidationProductFilter | null>(null);
  // What the user typed vs. the committed search the queries use (debounced, or Enter).
  const [searchDraft, setSearchDraft] = useState('');
  const [q, setQ] = useState<string | null>(null);
  const [rule, setRule] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [sort, setSort] = useState<EnrichmentValidationProductSort>('external_id');
  const [page, setPage] = useState(1);
  const panelId = useId();
  const searchId = useId();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Inside the drawer the Radix title / description label the dialog; inside a host's panel
  // the host owns the dialog, so plain elements (a second Radix title would clash).
  const Header = variant === 'sheet' ? SheetHeader : 'div';
  const Title = variant === 'sheet' ? SheetTitle : 'h3';
  const Description = variant === 'sheet' ? SheetDescription : 'p';

  const run = source.kind === 'run' ? source : null;
  const scoped = source.kind === 'scopes' ? source : null;
  // One product's "Fix" — needs workflow-service's product-scoped repair (#1010), so it stays
  // behind a flag: an older service ignores `productIds` and would repair the whole run.
  const singleFixFlag = useFeatureFlag(REPAIR_PRODUCT_SCOPE_FLAG);
  const singleFixOn = singleFixFlag && run !== null;
  const { startRevalidationWatch } = useEnrichmentValidationSummary(
    run?.workflowId ?? '',
    connectorCode,
    singleFixOn,
  );
  const repairOne = useRepairEnrichmentRun(
    run?.workflowId ?? '',
    run?.workflowId ?? '',
    startRevalidationWatch,
  );
  const singleFix = singleFixOn ? repairOne : null;
  const summary = run?.summary;
  const scopes = scoped?.scopes ?? NO_SCOPES;
  const digest = scoped?.digest ?? '';
  // Tab counts: a run's from its summary; a cross-run list's from the facets read itself (its
  // `statusCounts` ignore `status`). Read under the tab the user chose, else "needs normalising"
  // — the usual default — so the menus and the counts are ONE request whenever the shown tab is
  // that one (same key, deduped); only a list with nothing to normalise reads a second tab.
  const countsStatus = chosenStatus ?? 'invalid';
  const scopedCountsFacets = useScopedValidationFacets(
    scopes,
    connectorCode,
    digest,
    { status: countsStatus, q },
    active && scoped !== null,
  );
  const counts: Record<EnrichmentValidationProductFilter, number> | null = summary
    ? countsOf(summary)
    : (scopedCountsFacets.data?.statusCounts ?? null);
  const status: EnrichmentValidationProductFilter =
    chosenStatus ?? (counts ? defaultProductFilter(counts) : 'invalid');
  // A cross-run list learns its counts from a request (a run's come with its summary), so it
  // waits for them before reading a page: the tab is settled once, never shown then swapped.
  const tabSettled = scoped === null || chosenStatus !== null || counts !== null;
  const filters = { status, q, rule, field, reenrich: null, sort, page };

  const runQuery = useEnrichmentValidationProducts(
    run?.workflowId ?? '',
    connectorCode,
    summary,
    filters,
    active && run !== null,
  );
  const scopedQuery = useScopedValidatedProducts(
    scopes,
    connectorCode,
    digest,
    filters,
    active && scoped !== null && tabSettled,
  );
  const query = scoped ? scopedQuery : runQuery;
  const runFacets = useEnrichmentValidationFacets(
    run?.workflowId ?? '',
    connectorCode,
    summary,
    { status, q },
    active && run !== null,
  );
  const scopedFacets = useScopedValidationFacets(
    scopes,
    connectorCode,
    digest,
    { status, q },
    active && scoped !== null && tabSettled,
  );
  const facetsQuery = scoped ? scopedFacets : runFacets;
  const facets = facetsQuery.data;
  const facetState = facets ? 'ready' : facetsQuery.isError ? 'error' : 'loading';

  // One run's products, or a cross-run list's (each with the run it came from).
  const products: (EnrichmentValidatedProduct & { workflowId?: string })[] =
    query.data?.products ?? [];
  const pagination = query.data?.pagination;
  // No rows beside a non-zero total = a page past the end: the verdict set shrank under it
  // (OFFSET paging), so offer the way back rather than claim the list is empty.
  const isPastLastPage = products.length === 0 && (pagination?.total ?? 0) > 0;

  // Debounced search: commits the draft 300 ms after the last keystroke; Enter and Clear commit
  // at once (below). The timer only sets state, so it never runs synchronously in the effect.
  useEffect(() => {
    const next = normalizeSearch(searchDraft);
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
      scrollRef.current?.scrollTo({ top: 0 });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [searchDraft, q]);

  /** Every filter change restarts at page 1 at the top of the list — set in the handlers. */
  function resetToFirstPage() {
    setPage(1);
    scrollRef.current?.scrollTo({ top: 0 });
  }
  function commitSearch(raw: string) {
    const next = normalizeSearch(raw);
    if (next === q) return;
    setQ(next);
    resetToFirstPage();
  }
  function handleSearchChange(value: string) {
    setSearchDraft(value);
    if (value === '') commitSearch('');
  }
  function clearSearch() {
    setSearchDraft('');
    commitSearch('');
  }
  function handleStatusChange(next: EnrichmentValidationProductFilter) {
    setStatus(next);
    resetToFirstPage();
  }
  function handleRuleChange(next: string | null) {
    setRule(next);
    resetToFirstPage();
  }
  function handleFieldChange(next: string | null) {
    setField(next);
    resetToFirstPage();
  }
  function handleSortChange(next: string) {
    const value = SORTS.find((s) => s === next);
    if (!value) return;
    setSort(value);
    resetToFirstPage();
  }
  function clearMenuFilters() {
    setRule(null);
    setField(null);
    resetToFirstPage();
  }
  function handlePageChange(next: number) {
    setPage(next);
    scrollRef.current?.scrollTo({ top: 0 });
  }

  // Header summary: needs normalising and checked always, normalised / warnings when any.
  const summarySegments = counts
    ? [
        t('enrichmentValidation.drawer.summaryLine.failed', {
          count: counts.invalid,
          formatted: counts.invalid.toLocaleString(),
        }),
        ...(counts.warning > 0
          ? [
              t('enrichmentValidation.drawer.summaryLine.warning', {
                count: counts.warning,
                formatted: counts.warning.toLocaleString(),
              }),
            ]
          : []),
        ...(counts.normalised > 0
          ? [
              t('enrichmentValidation.drawer.summaryLine.normalised', {
                count: counts.normalised,
                formatted: counts.normalised.toLocaleString(),
              }),
            ]
          : []),
        t('enrichmentValidation.drawer.summaryLine.checked', {
          count: counts.all,
          formatted: counts.all.toLocaleString(),
        }),
      ]
    : [];

  // Filter menus, counted by the facets under the current status + search. When the facets
  // can't be read, the menus still offer values (no counts): the summary's top rules and the
  // known enriched columns, so filtering never depends on the counts endpoint.
  const facetsFailed = !facets && facetsQuery.isError;
  const fallbackRules: FacetOption[] = facetsFailed
    ? (summary?.topRules ?? []).map((r) => ({ value: r.rule, label: ruleLabel(r.rule) }))
    : [];
  const fallbackFields: FacetOption[] = facetsFailed
    ? FALLBACK_FIELDS[connectorCode].map((value) => ({ value, label: fieldLabel(value) }))
    : [];
  const ruleOptions = withSelected(
    facets
      ? facets.rules.map((r) => ({ value: r.value, label: ruleLabel(r.value), count: r.count }))
      : fallbackRules,
    rule,
    ruleLabel,
  );
  const fieldOptions = withSelected(
    facets
      ? facets.fields.map((f) => ({ value: f.value, label: fieldLabel(f.value), count: f.count }))
      : fallbackFields,
    field,
    fieldLabel,
  );

  // Applied menu filters as removable chips (search has its own clear button). Plain data:
  // the remove handler is looked up by key on click, never captured during render.
  type ChipKey = 'rule' | 'field';
  const chips: { key: ChipKey; label: string; value: string }[] = [];
  if (rule !== null) {
    chips.push({
      key: 'rule',
      label: t('enrichmentValidation.drawer.filters.problem'),
      value: ruleLabel(rule),
    });
  }
  if (field !== null) {
    chips.push({
      key: 'field',
      label: t('enrichmentValidation.drawer.filters.field'),
      value: fieldLabel(field),
    });
  }
  function removeChip(key: ChipKey) {
    if (key === 'rule') handleRuleChange(null);
    else handleFieldChange(null);
  }
  const hasMenuFilters = chips.length > 0;

  const emptyMessage =
    q !== null
      ? hasMenuFilters
        ? t('enrichmentValidation.drawer.empty.searchFiltered', { q })
        : t('enrichmentValidation.drawer.empty.search', { q })
      : hasMenuFilters
        ? t('enrichmentValidation.drawer.empty.filtered')
        : t(`enrichmentValidation.drawer.empty.${status}`);

  // Recovery actions. "Back to first page" is navigation like the pager's own buttons (the list
  // skeletons while it loads); "Try again" re-reads the same page with its own spinner.
  const firstPageButton = (
    <Button
      data-testid="batch-detail-validation-drawer-first-page-button"
      variant="outline"
      size="sm"
      className="cursor-pointer"
      onClick={() => {
        handlePageChange(1);
      }}
    >
      {t('enrichmentValidation.drawer.firstPage')}
    </Button>
  );
  const retryButton = (
    <Button
      data-testid="batch-detail-validation-drawer-retry-button"
      variant="outline"
      size="sm"
      className="cursor-pointer disabled:cursor-not-allowed"
      isLoading={query.isFetching}
      onClick={() => {
        // Never rejects (no `throwOnError`); a repeat failure keeps the error state.
        void query.refetch();
      }}
    >
      {t('enrichmentValidation.drawer.retry')}
    </Button>
  );

  // "Showing 1–50 of 312,418": from the requested page, so it moves with the pager at once.
  const total = pagination?.total ?? 0;
  const rangeStart = Math.min((page - 1) * ENRICHMENT_VALIDATION_PAGE_SIZE + 1, total);
  const rangeEnd = Math.min(page * ENRICHMENT_VALIDATION_PAGE_SIZE, total);
  // Remounts the row list per page / filter set, so expand state starts fresh on each.
  const listKey = JSON.stringify([status, q, rule, field, sort, page]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header className={cn('space-y-2 border-b px-5 py-4', variant === 'sheet' && 'pr-12')}>
        {/* Title left, export right; the header's pr-12 keeps it clear of the close button. */}
        <div className="flex items-center justify-between gap-3">
          <Title
            data-testid="batch-detail-validation-drawer-title"
            className="text-base font-semibold"
          >
            {t('enrichmentValidation.drawer.title')}
          </Title>
          {/* The CSV export is per run (built by the export service from the run's list). */}
          {run && (
            <ValidationExportButton
              workflowId={run.workflowId}
              connectorCode={connectorCode}
              status={status}
              q={q}
              rule={rule}
              field={field}
              sort={sort}
              count={query.isFetching || !pagination ? undefined : pagination.total}
            />
          )}
        </div>
        <Description className="sr-only">
          {t('enrichmentValidation.drawer.description')}
        </Description>
        {summarySegments.length > 0 && (
          <p
            data-testid="batch-detail-validation-drawer-summary"
            className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs tabular-nums"
          >
            {summarySegments.map((segment, index) => (
              <span key={segment} className="flex items-center gap-x-2">
                {index > 0 && <span aria-hidden="true">·</span>}
                {segment}
              </span>
            ))}
          </p>
        )}
      </Header>

      <div className="space-y-2 border-b px-5 py-3">
        {/* Tabs keep their natural width; past the sheet's width they scroll, never clip. */}
        <div className="max-w-full min-w-0 overflow-x-auto">
          <SegmentedTabs
            value={status}
            onChange={handleStatusChange}
            ariaLabel={t('enrichmentValidation.drawer.tabsLabel')}
            panelId={panelId}
            size="sm"
            className="bg-selected-row w-max border-0 sm:max-w-none"
            testId="batch-detail-validation-drawer-tabs"
            options={FILTERS.map((value) => ({
              value,
              tabId: `${panelId}-tab-${value}`,
              label: t(`enrichmentValidation.drawer.tabs.${value}`),
              ...(counts ? { badge: counts[value].toLocaleString() } : {}),
              // Error tone only where the count is a real problem (needs normalising).
              ...(value === 'invalid' && counts && counts.invalid > 0
                ? { badgeTone: 'problem' as const }
                : {}),
            }))}
          />
        </div>
        <div
          role="group"
          aria-label={t('enrichmentValidation.drawer.filterBarLabel')}
          className="flex flex-wrap items-center gap-2"
        >
          {/* Search leads the filter row; a form so Enter commits at once (Clear is type=button). */}
          <form
            role="search"
            data-testid="batch-detail-validation-drawer-search"
            className="min-w-0 flex-1 basis-56"
            onSubmit={(event) => {
              event.preventDefault();
              commitSearch(searchDraft);
            }}
          >
            <label htmlFor={searchId} className="sr-only">
              {t('enrichmentValidation.drawer.searchLabel')}
            </label>
            <SearchInput
              id={searchId}
              testId="batch-detail-validation-drawer-search-field"
              value={searchDraft}
              onChange={handleSearchChange}
              placeholder={t('enrichmentValidation.drawer.searchPlaceholder')}
              inputClassName="h-8 text-sm"
            />
          </form>
          <FacetMenu
            testId="batch-detail-validation-drawer-problem-filter"
            label={t('enrichmentValidation.drawer.filters.problem')}
            allLabel={t('enrichmentValidation.drawer.allRules')}
            value={rule}
            selectedLabel={rule === null ? null : ruleLabel(rule)}
            options={ruleOptions}
            truncated={facets?.rulesTruncated ?? false}
            state={facetState}
            onChange={handleRuleChange}
          />
          <FacetMenu
            testId="batch-detail-validation-drawer-field-filter"
            label={t('enrichmentValidation.drawer.filters.field')}
            allLabel={t('enrichmentValidation.drawer.filters.allFields')}
            value={field}
            selectedLabel={field === null ? null : fieldLabel(field)}
            options={fieldOptions}
            truncated={facets?.fieldsTruncated ?? false}
            state={facetState}
            onChange={handleFieldChange}
          />
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="batch-detail-validation-drawer-sort-select"
                  variant="ghost"
                  size="sm"
                  className="h-8 cursor-pointer gap-1.5 px-2.5 text-xs"
                >
                  <ArrowUpDown aria-hidden="true" className="size-3.5" />
                  {t('enrichmentValidation.drawer.sortTrigger', {
                    label: t(`enrichmentValidation.drawer.sort.${sort}`),
                  })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                data-testid="batch-detail-validation-drawer-sort-menu"
                align="end"
                className="w-52"
              >
                <DropdownMenuLabel className="text-muted-foreground text-xs font-medium">
                  {t('enrichmentValidation.drawer.sortLabel')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  data-testid="batch-detail-validation-drawer-sort-radio-group"
                  value={sort}
                  onValueChange={handleSortChange}
                >
                  {SORTS.map((value) => (
                    <DropdownMenuRadioItem
                      key={value}
                      data-testid={`batch-detail-validation-drawer-sort-item-${value}`}
                      value={value}
                      className="cursor-pointer"
                    >
                      {t(`enrichmentValidation.drawer.sort.${value}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {hasMenuFilters && (
          <div className="flex flex-wrap items-center gap-2">
            <ul
              aria-label={t('enrichmentValidation.drawer.filters.active')}
              className="flex flex-wrap items-center gap-2"
            >
              {chips.map((chip) => (
                <li key={chip.key}>
                  <Button
                    data-testid={`batch-detail-validation-drawer-filter-chip-remove-button-${chip.key}`}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      removeChip(chip.key);
                    }}
                    aria-label={t('enrichmentValidation.drawer.filters.remove', {
                      label: chip.label,
                      value: chip.value,
                    })}
                    className="h-7 max-w-[18rem] cursor-pointer gap-1.5 rounded-full px-2.5 text-xs font-normal"
                  >
                    <span className="text-muted-foreground shrink-0">{chip.label}</span>
                    <span className="truncate font-medium">{chip.value}</span>
                    <X aria-hidden="true" className="text-muted-foreground size-3" />
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              data-testid="batch-detail-validation-drawer-clear-all-filters-button"
              variant="ghost"
              size="sm"
              onClick={clearMenuFilters}
              className="h-7 cursor-pointer px-2 text-xs"
            >
              {t('enrichmentValidation.drawer.filters.clearAll')}
            </Button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${panelId}-tab-${status}`}
        data-testid={`batch-detail-validation-drawer-panel-${status}`}
        className="bg-background flex-1 overflow-y-auto px-5 py-1"
      >
        {/* `isPlaceholderData` = the previous page/filter is on screen while the
         *  new one loads — skeleton the list rather than show stale rows. */}
        {query.isLoading || query.isPlaceholderData || !tabSettled ? (
          <div
            data-testid="batch-detail-validation-drawer-loading"
            className="space-y-2 py-3"
            aria-busy="true"
          >
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="flex items-start gap-3 py-1">
                <Skeleton className="size-12 shrink-0 rounded-md" />
                <div className="flex-1 space-y-2 pt-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <div
            data-testid="batch-detail-validation-drawer-error"
            className="flex flex-col items-center gap-3 py-8 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t('enrichmentValidation.drawer.loadFailed')}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {retryButton}
              {page > 1 && firstPageButton}
            </div>
          </div>
        ) : isPastLastPage ? (
          <div
            data-testid="batch-detail-validation-drawer-out-of-range"
            className="flex flex-col items-center gap-3 py-8 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t('enrichmentValidation.drawer.pageOutOfRange')}
            </p>
            {/* Page 1 can only get here in a mid-run count/page race: re-read it instead. */}
            {page > 1 ? firstPageButton : retryButton}
          </div>
        ) : products.length === 0 ? (
          <div
            data-testid="batch-detail-validation-drawer-empty"
            className="flex flex-col items-center gap-3 py-8 text-center"
          >
            <p className="text-muted-foreground text-sm break-words">{emptyMessage}</p>
            {(q !== null || hasMenuFilters) && (
              <div className="flex flex-wrap justify-center gap-2">
                {q !== null && (
                  <Button
                    data-testid="batch-detail-validation-drawer-clear-search-button"
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    onClick={clearSearch}
                  >
                    {t('enrichmentValidation.drawer.clearSearch')}
                  </Button>
                )}
                {hasMenuFilters && (
                  <Button
                    data-testid="batch-detail-validation-drawer-clear-filters-button"
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    onClick={clearMenuFilters}
                  >
                    {t('enrichmentValidation.drawer.clearFilters')}
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <ul
            key={listKey}
            data-testid="batch-detail-validation-drawer-list"
            className="divide-border divide-y"
          >
            {products.map((product) => (
              // `externalId` is the verdict dedupe key, so it is unique per list. A page of
              // exactly one product opens it; otherwise every row starts collapsed.
              <ValidatedProductRow
                // A cross-run list can hold the same external id once per run.
                key={`${product.workflowId ?? ''}:${product.externalId}`}
                product={product}
                defaultExpanded={products.length === 1}
                batchName={
                  scoped && product.workflowId ? scoped.batchName(product.workflowId) : null
                }
                {...(singleFix
                  ? {
                      onFix: () => {
                        singleFix.mutate([product.productId]);
                      },
                      fixPending:
                        singleFix.isPending && singleFix.variables?.[0] === product.productId,
                    }
                  : {})}
              />
            ))}
          </ul>
        )}
      </div>

      {pagination && total > 0 && !query.isError && (
        <div
          data-testid="batch-detail-validation-drawer-pagination"
          className="bg-background flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3"
        >
          <span
            role="status"
            data-testid="batch-detail-validation-drawer-showing"
            aria-live="polite"
            className="text-muted-foreground text-xs tabular-nums"
          >
            {t('enrichmentValidation.drawer.showing', {
              start: rangeStart.toLocaleString(),
              end: rangeEnd.toLocaleString(),
              total: total.toLocaleString(),
            })}
          </span>
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            testId="batch-detail-validation-drawer-pagination-controls"
          />
        </div>
      )}
    </div>
  );
}

/** "View products" as a side drawer — `InvalidProductsView` in a sheet, focus returned to the
 *  button that opened it. */
export function InvalidProductsDrawer({
  open,
  onOpenChange,
  workflowId,
  connectorCode,
  summary,
}: InvalidProductsDrawerProps) {
  // What had focus when the drawer opened (its View-products button). Radix restores focus only
  // to a SheetTrigger, which this drawer doesn't have, so `onCloseAutoFocus` returns it here.
  const openerRef = useRef<HTMLElement | null>(null);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="batch-detail-validation-drawer"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl lg:max-w-3xl"
        onOpenAutoFocus={() => {
          // Fired before Radix moves focus into the sheet, so the opener still holds it.
          openerRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          const opener = openerRef.current;
          // A detached opener (its banner state changed meanwhile) keeps Radix's default.
          if (!opener?.isConnected) return;
          event.preventDefault();
          opener.focus();
        }}
      >
        <InvalidProductsView
          source={{ kind: 'run', workflowId, summary }}
          connectorCode={connectorCode}
          active={open}
        />
      </SheetContent>
    </Sheet>
  );
}
